import _pick from "lodash/pick";
import * as qs from "query-string";
import * as router from "react-router-redux";
import { denormalize } from "normalizr";
import { createSelector } from "reselect";
import { actionPrefix } from "./constants";
import * as ResultsEvents from "./tracker/event-names/results-events";
import { trackEvent } from "./tracker/actions";
import { api, getApiState } from "./api";
import { PROGRESS } from "./asyncHttpAction";
import { pageResultsSchema } from "../schemas";

const prefix = `${actionPrefix}/results`;

export const actions = {
    SELECT_PAGE: `${prefix}/SELECT_PAGE`,
    SELECT_EXPECTATION: `${prefix}/SELECT_EXPECTATION`,
    SET_SELECTED_PAGES: `${prefix}/SET_SELECTED_PAGES`,
    SHOW_COMPARE_PAGES_GRAPH: `${prefix}/SHOW_COMPARE_PAGES_GRAPH`,
    TOGGLE_PAGE_SELECTION: `${prefix}/TOGGLE_PAGE_SELECTION`,
    OPEN_SHARE_LINK_MODAL: `${prefix}/OPEN_SHARE_LINK_MODAL`,
    CLOSE_SHARE_LINK_MODAL: `${prefix}/CLOSE_SHARE_LINK_MODAL`,
    RESET_STATE: `${prefix}/RESET_STATE`
};

function getPageIdProp(state, props) {
    return props.pageId;
}

export const getPageResults = createSelector(
    [getApiState, getPageIdProp],
    (api, pageId) => denormalize(pageId, pageResultsSchema, api)
);

export function getDriverOutliers(pageDrivers) {
    if (!pageDrivers) {
        return null;
    }
    let outlierSegments = [];
    for (let pageDriver of pageDrivers) {
        if (!pageDriver.segmentScores) {
            continue;
        }
        outlierSegments = [
            ...outlierSegments,
            ...pageDriver.segmentScores
                .filter(s => Math.abs(s.score - pageDriver.driverScore) > 4)
                .map(s => ({
                    ...s,
                    driver: {
                        name: pageDriver.driver,
                        score: pageDriver.driverScore
                    }
                }))
        ];
    }
    return outlierSegments.length > 0 ? outlierSegments : null;
}

export function changeBaseline(pageId) {
    return async (dispatch, getState) => {
        const queryString = qs.stringify({
            ...getState().entities.queryString,
            ...{ baseline: pageId }
        });
        return dispatch(router.replace({ search: `?${queryString}` }));
    };
}

export function selectPage(pageId) {
    return async (dispatch, getState) => {
        const queryString = qs.stringify({
            ...getState().entities.queryString,
            ...{ selected: pageId }
        });
        dispatch(trackPageSelect(pageId));
        return dispatch(router.replace({ search: `?${queryString}` }));
    };
}

function trackPageSelect(pageId) {
    return (dispatch, getState) => {
        const { wevos } = getState().entities;
        const { selectedPageId } = getState().results;
        const properties = ["id", "name", "is_baseline", "is_competitor"];
        return dispatch(
            trackEvent(ResultsEvents.DID_TOGGLE_SELECTED_PAGE, {
                from_page: _pick(wevos[selectedPageId], properties),
                to_page: _pick(wevos[pageId], properties)
            })
        );
    };
}

export function selectExpectation(expectationId) {
    return {
        type: actions.SELECT_EXPECTATION,
        payload: { expectationId }
    };
}

export function setSelectedPages(pageIds) {
    return {
        type: actions.SET_SELECTED_PAGES,
        payload: {
            pageIds
        }
    };
}

export function setShowGraphOnComparePagesScreen(showGraph) {
    return {
        type: actions.SHOW_COMPARE_PAGES_GRAPH,
        payload: { showGraph }
    };
}

export function togglePageSelection(pageId) {
    return {
        type: actions.TOGGLE_PAGE_SELECTION,
        payload: {
            pageId
        }
    };
}

export function openShareLinkModal() {
    return {
        type: actions.OPEN_SHARE_LINK_MODAL
    };
}

export function closeShareLinkModal() {
    return {
        type: actions.CLOSE_SHARE_LINK_MODAL
    };
}

export function resetResultsState() {
    return {
        type: actions.RESET_STATE
    };
}

const INITIAL_STATE = {
    pageResultRequest: PROGRESS.initial,
    shareLinkModalOpen: false,
    selectedExpectationId: null,
    selectedPageIds: [],
    showGraph: true,
    wevoId: null,
    wevoRequest: PROGRESS.initial,
    wevoResultsRequest: PROGRESS.initial
};

export function reducer(state = INITIAL_STATE, action) {
    switch (action.type) {
        case api.wevoShareLinks.get.REQUEST:
        case api.wevos.get.REQUEST:
            return {
                ...state,
                wevoRequest: PROGRESS.loading
            };

        case api.wevoShareLinks.get.SUCCESS:
        case api.wevos.get.SUCCESS:
            return {
                ...state,
                wevoId: action.payload.data.result,
                wevoRequest: PROGRESS.success
            };

        case api.wevoShareLinks.get.ERROR:
        case api.wevos.get.ERROR:
            return {
                ...state,
                wevoRequest: PROGRESS.error
            };

        case api.wevos.results.REQUEST:
        case api.wevoShareLinks.results.REQUEST:
            return {
                ...state,
                wevoResultsRequest: PROGRESS.loading
            };

        case api.wevos.results.SUCCESS:
        case api.wevoShareLinks.results.SUCCESS:
            return {
                ...state,
                wevoId: action.payload.data.result,
                wevoResultsRequest: PROGRESS.success
            };

        case api.wevos.results.ERROR:
        case api.wevoShareLinks.results.ERROR:
            return {
                ...state,
                wevoResultsRequest: PROGRESS.error
            };

        case api.wevos.pages.results.REQUEST:
        case api.wevoShareLinks.pages.results.REQUEST:
            return {
                ...state,
                pageResultRequest: PROGRESS.loading
            };

        case api.wevos.pages.results.SUCCESS:
        case api.wevoShareLinks.pages.results.SUCCESS:
            return {
                ...state,
                pageResultRequest: PROGRESS.success
            };

        case api.wevos.pages.results.ERROR:
        case api.wevoShareLinks.pages.results.ERROR:
            return {
                ...state,
                pageResultRequest: PROGRESS.error
            };

        case actions.OPEN_SHARE_LINK_MODAL:
            return {
                ...state,
                shareLinkModalOpen: true
            };

        case actions.CLOSE_SHARE_LINK_MODAL:
            return {
                ...state,
                shareLinkModalOpen: false
            };

        case actions.SET_SELECTED_PAGES:
            return {
                ...state,
                selectedPageIds: action.payload.pageIds
            };

        case actions.SHOW_COMPARE_PAGES_GRAPH:
            return {
                ...state,
                showGraph: action.payload.showGraph
            };

        case actions.TOGGLE_PAGE_SELECTION:
            const pageId = action.payload.pageId;
            const index = state.selectedPageIds.indexOf(pageId);

            const selectedPageIds = [...state.selectedPageIds];
            if (index >= 0) {
                selectedPageIds.splice(index, 1);
            } else {
                selectedPageIds.push(pageId);
            }

            return {
                ...state,
                selectedPageIds
            };

        case actions.SELECT_EXPECTATION:
            return {
                ...state,
                selectedExpectationId: action.payload.expectationId
            };

        case actions.RESET_STATE:
            return INITIAL_STATE;

        default:
            return state;
    }
}



// WEBPACK FOOTER //
// ./src/modules/results.js